import { useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import SchemaOrg from '@/components/base/SchemaOrg';
import { articles, categories } from '@/mocks/articles';
import CategoryNav from './components/CategoryNav';
import ArticleCard from './components/ArticleCard';

const categoryImageUrls: Record<string, string> = {
  'xinshou-bidu-hexin-caozuo': 'https://readdy.ai/api/search-image?query=A%20beginner%20adventurer%20learning%20to%20jump%20on%20a%20small%20mushroom%20in%20a%20safe%20fantasy%20forest%2C%20training%20area%20with%20signs%2C%20hand-drawn%20illustration%20style%2C%20soft%20warm%20lighting%2C%20encouraging%20atmosphere%2C%20bright%20colors&width=600&height=400&seq=cat-art-01&orientation=landscape',
  'guanqia-1-5-tongguan-gonglue': 'https://readdy.ai/api/search-image?query=A%20game%20level%20map%20showing%20mushroom%20platforms%20and%20paths%20in%20a%20fantasy%20forest%2C%20top-down%20view%2C%20hand-drawn%20illustration%20style%2C%20warm%20colors%2C%20exploration%20theme%2C%20soft%20lighting&width=600&height=400&seq=cat-art-02&orientation=landscape',
  'gaofen-gonglue-shi-da-jiqiao': 'https://readdy.ai/api/search-image?query=A%20scoreboard%20with%20high%20scores%20displayed%20on%20a%20giant%20mushroom%2C%20celebration%20confetti%2C%20hand-drawn%20illustration%20style%2C%20champion%20theme%2C%20golden%20lighting%2C%20festive%20atmosphere&width=600&height=400&seq=cat-art-03&orientation=landscape',
  'jinjie-jiqiao-fantan-caiqiang': 'https://readdy.ai/api/search-image?query=An%20acrobatic%20adventurer%20performing%20advanced%20wall%20jump%20moves%20in%20a%20challenging%20fantasy%20forest%20obstacle%20course%2C%20dynamic%20action%2C%20hand-drawn%20illustration%2C%20warm%20lighting%2C%20exciting%20mood&width=600&height=400&seq=cat-art-04&orientation=landscape',
  'yincang-caidan-yincang-guanka': 'https://readdy.ai/api/search-image?query=A%20mysterious%20glowing%20portal%20hidden%20inside%20a%20giant%20hollow%20mushroom%2C%20secret%20passage%2C%20hand-drawn%20illustration%20style%2C%20magical%20atmosphere%2C%20discovery%20theme%2C%20warm%20mysterious%20light&width=600&height=400&seq=cat-art-05&orientation=landscape',
  'guanqia-6-10-wanzheng-gonglue': 'https://readdy.ai/api/search-image?query=A%20challenging%20boss%20battle%20arena%20with%20a%20giant%20mushroom%20monster%2C%20epic%20fantasy%20scene%2C%20hand-drawn%20illustration%2C%20dramatic%20warm%20lighting%2C%20intense%20action%20mood&width=600&height=400&seq=cat-art-06&orientation=landscape',
  'yidongduan-caozuo-youhua': 'https://readdy.ai/api/search-image?query=A%20mobile%20phone%20displaying%20a%20colorful%20mushroom%20game%20on%20screen%2C%20held%20by%20hands%2C%20soft%20warm%20lighting%2C%20cozy%20setting%2C%20hand-drawn%20illustration%20style%2C%20modern%20casual%20gaming%20theme&width=600&height=400&seq=cat-art-07&orientation=landscape',
  'sutong-jilu-jiexi': 'https://readdy.ai/api/search-image?query=A%20speedrunner%20adventurer%20dashing%20through%20mushroom%20platforms%20with%20motion%20blur%20effects%2C%20fast%20action%2C%20hand-drawn%20illustration%2C%20dynamic%20composition%2C%20warm%20racing%20colors&width=600&height=400&seq=cat-art-08&orientation=landscape',
  'changjian-bug-jiejue-fangfa': 'https://readdy.ai/api/search-image?query=A%20repair%20toolkit%20and%20wrench%20next%20to%20a%20colorful%20mushroom%20in%20a%20fantasy%20workshop%2C%20hand-drawn%20illustration%2C%20warm%20lighting%2C%20problem-solving%20theme%2C%20cozy%20crafting%20atmosphere&width=600&height=400&seq=cat-art-09&orientation=landscape',
  'yincang-guanka-daozhi-shijie': 'https://readdy.ai/api/search-image?query=A%20surreal%20upside-down%20fantasy%20world%20with%20mushrooms%20growing%20from%20the%20sky%2C%20gravity-bending%20scene%2C%20hand-drawn%20illustration%2C%20magical%20surreal%20lighting%2C%20mind-bending%20atmosphere&width=600&height=400&seq=cat-art-10&orientation=landscape',
  'chengjiu-huode-zhinan': 'https://readdy.ai/api/search-image?query=A%20collection%20of%20shiny%20achievement%20badges%20and%20trophies%20displayed%20on%20mushroom%20pedestals%2C%20hand-drawn%20illustration%2C%20celebration%20theme%2C%20golden%20warm%20lighting%2C%20proud%20atmosphere&width=600&height=400&seq=cat-art-11&orientation=landscape',
  'banben-chayi-duibi': 'https://readdy.ai/api/search-image?query=Three%20different%20art%20styles%20of%20the%20same%20mushroom%20character%20side%20by%20side%2C%20pixel%20art%20to%20hand-drawn%2C%20evolution%20theme%2C%20hand-drawn%20illustration%2C%20warm%20lighting%2C%20comparison%20concept&width=600&height=400&seq=cat-art-12&orientation=landscape',
};

export default function CategoryPage() {
  const [searchParams] = useSearchParams();
  const activeCat = searchParams.get('cat') || '';

  const filteredArticles = activeCat
    ? articles.filter((a) => a.category === activeCat)
    : articles;

  const activeCategory = categories.find((c) => c.id === activeCat);
  const pageTitle = activeCategory
    ? `${activeCategory.name} - 踩蘑菇游戏攻略`
    : '攻略分类 - 踩蘑菇游戏攻略大全';
  const pageDesc = activeCategory
    ? `踩蘑菇${activeCategory.name}专题，${activeCategory.desc}。包含${filteredArticles.length}篇精选攻略文章，助你成为蘑菇大师。`
    : '浏览踩蘑菇全部攻略分类，包括玩法技巧、关卡攻略、高分秘籍、新手教程、进阶技巧和隐藏要素。';

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={pageTitle}
        description={pageDesc}
        keywords={`踩蘑菇,${activeCategory ? activeCategory.name : '攻略'},踩蘑菇游戏攻略,踩蘑菇玩法技巧`}
        canonicalUrl={`https://jlsdfdl.cn/category${activeCat ? `?cat=${activeCat}` : ''}`}
      />
      <SchemaOrg type="WebPage" data={{
        name: pageTitle,
        description: pageDesc,
        url: `https://jlsdfdl.cn/category${activeCat ? `?cat=${activeCat}` : ''}`,
        inLanguage: 'zh-CN',
      }} />
      <Navbar />
      <main className="pt-24 pb-14 md:pb-16 bg-background-50 min-h-screen">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb items={[
            { label: '首页', href: '/' },
            { label: '攻略分类' },
            ...(activeCategory ? [{ label: activeCategory.name }] : []),
          ]} />

          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-4">
              {activeCategory ? activeCategory.name : '全部攻略'}
            </h1>
            {activeCategory && (
              <p className="text-sm text-foreground-500 mb-5">{activeCategory.desc}</p>
            )}
            <CategoryNav />
          </div>

          <div className="space-y-4">
            {filteredArticles.length > 0 ? (
              filteredArticles.map((article) => (
                <ArticleCard
                  key={article.id}
                  id={article.id}
                  title={article.title}
                  slug={article.slug}
                  excerpt={article.excerpt}
                  difficulty={article.difficulty}
                  author={article.author}
                  date={article.date}
                  readTime={article.readTime}
                  viewCount={article.viewCount}
                  imageUrl={categoryImageUrls[article.slug] || categoryImageUrls['xinshou-bidu-hexin-caozuo']}
                />
              ))
            ) : (
              <div className="text-center py-20">
                <span className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
                  <i className="ri-file-search-line text-2xl text-foreground-400"></i>
                </span>
                <p className="text-foreground-500 text-sm">暂无相关文章，请选择其他分类</p>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}